import { Component, EventEmitter, Input, Output, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../../services/auth/auth.service';

@Component({
  selector: 'app-confirm-sign-in',
  templateUrl: './confirm-sign-in.component.html',
  styleUrl: './confirm-sign-in.component.scss',
  standalone: false,
})
export class ConfirmSignInComponent implements OnDestroy {
  @Input() showDialog: boolean = false; // Controla la visibilidad del dialog
  @Input() userInformation: { email: string; password: string } = { email: '', password: '' };
  @Input() sessionToken: string = ''; // Token de sesión
  @Output() passwordChanged = new EventEmitter<string>(); // Emitir cuando la contraseña haya cambiado
  changePasswordForm: FormGroup<any>;
  loading: boolean = false;

  passwordFieldType: string = 'password';
  passwordCriteria = {
    minLength: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecial: false,
  };
  private subscriptions: Subscription[] = [];
  validationMessages = {
    newPassword: [
      { type: 'required', msg: 'Este campo es requerido.' },
      { type: 'minlength', msg: 'Debe tener mínimo de 12 caracteres de largo.' },
      { type: 'pattern', msg: 'Debe incluir 1 número, 1 mayúscula y 1 carácter especial.' },
    ],
    confirmNewPassword: [
      { type: 'required', msg: 'Este campo es requerido.' },
      { type: 'minlength', msg: 'Debe tener mínimo de 12 caracteres de largo.' },
      { type: 'pattern', msg: 'Debe incluir 1 número, 1 mayúscula y 1 carácter especial.' },
    ],
  };

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder
  ) {
    const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/;

    this.changePasswordForm = this.formBuilder.group(
      {
        newPassword: ['', Validators.compose([Validators.required, Validators.minLength(12), Validators.pattern(PASSWORD_PATTERN)])],
        confirmNewPassword: ['', Validators.compose([Validators.required, Validators.minLength(12), Validators.pattern(PASSWORD_PATTERN)])],
      },
      { validators: [this.passwordsMatchValidator()] }
    );

    const sub = this.changePasswordForm.get('newPassword')?.valueChanges.subscribe((value: string) => {
      this.evaluatePassword(value || '');
    });
    if (sub) this.subscriptions.push(sub);
  }

  onSubmitNewPassword() {
    if (this.changePasswordForm.valid && this.changePasswordForm.value.newPassword === this.changePasswordForm.value.confirmNewPassword) {
      this.authService
        .forceChangePassword(this.userInformation.email, this.changePasswordForm.value.newPassword, this.sessionToken)
        .then(() => {
          this.passwordChanged.emit(this.changePasswordForm.value.newPassword); // Emitir evento cuando la contraseña cambie exitosamente
          this.showDialog = false;
        })
        .catch((error) => {
          console.error('Error al cambiar la contraseña:', error);
        });
    } else {
      console.error('Las contraseñas no coinciden');
    }
  }

  // Alternar la visibilidad de la contraseña
  togglePasswordVisibility() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  private passwordsMatchValidator(): ValidatorFn {
    return (group: AbstractControl) => {
      const newPassword = group.get('newPassword')?.value;
      const confirmNewPassword = group.get('confirmNewPassword')?.value;
      if (!newPassword || !confirmNewPassword) return null;
      return newPassword === confirmNewPassword ? null : { passwordMismatch: true };
    };
  }

  private evaluatePassword(value: string): void {
    this.passwordCriteria.minLength = value.length >= 12;
    this.passwordCriteria.hasUppercase = /[A-Z]/.test(value);
    this.passwordCriteria.hasNumber = /\d/.test(value);
    this.passwordCriteria.hasSpecial = /[\W_]/.test(value);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
